// Frontend validation utilities
export const ValidationRules = {
  email: [
    { required: true, message: 'Email is required' },
    { type: 'email', message: 'Please enter a valid email address' },
    { max: 100, message: 'Email must be less than 100 characters' },
    {
      pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: 'Please enter a valid email format'
    }
  ],
  
  password: [
    { required: true, message: 'Password is required' },
    { min: 8, message: 'Password must be at least 8 characters long' },
    { max: 128, message: 'Password must be less than 128 characters' },
    {
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }
  ],
  
  newPassword: [
    { required: true, message: 'New password is required' },
    { min: 8, message: 'Password must be at least 8 characters long' },
    { max: 128, message: 'Password must be less than 128 characters' },
    {
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    }
  ],
  
  confirmPassword: (getFieldValue: any) => [
    { required: true, message: 'Please confirm your password' },
    {
      validator: (_: any, value: string) => {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Passwords do not match'));
      }
    }
  ],
  
  confirmNewPassword: (getFieldValue: any) => [
    { required: true, message: 'Please confirm your new password' },
    {
      validator: (_: any, value: string) => {
        if (!value || getFieldValue('newPassword') === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Passwords do not match'));
      }
    }
  ],

  name: [
    { required: true, message: 'Name is required' },
    { min: 2, message: 'Name must be at least 2 characters' },
    { max: 50, message: 'Name must be less than 50 characters' },
    {
      pattern: /^[a-zA-Z\s]+$/,
      message: 'Name can only contain letters and spaces'
    }
  ],

  clientName: [
    { required: true, message: 'Client name is required' },
    { min: 2, message: 'Client name must be at least 2 characters' },
    { max: 100, message: 'Client name must be less than 100 characters' },
    {
      pattern: /^[a-zA-Z0-9\s\-_.]+$/,
      message: 'Client name can only contain letters, numbers, spaces, hyphens, underscores, and periods'
    }
  ]
};

// Input sanitization utilities
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential XSS characters
    .substring(0, 1000); // Limit length
};

export const sanitizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

// Validation helper functions
export const validatePasswordStrength = (password: string): {
  score: number;
  feedback: string[];
} => {
  let score = 0;
  const feedback: string[] = [];

  if (password.length >= 8) score += 1;
  else feedback.push('At least 8 characters');

  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('One lowercase letter');

  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('One uppercase letter');

  if (/\d/.test(password)) score += 1;
  else feedback.push('One number');

  if (/[@$!%*?&]/.test(password)) score += 1;
  else feedback.push('One special character (@$!%*?&)');

  return { score, feedback };
};

export const getPasswordStrengthColor = (score: number): string => {
  if (score < 2) return '#ff4d4f'; // Red
  if (score < 4) return '#faad14'; // Orange
  return '#52c41a'; // Green
};

export const getPasswordStrengthText = (score: number): string => {
  if (score < 2) return 'Weak';
  if (score < 4) return 'Medium';
  return 'Strong';
};

// Password validation function for accept invitation
export const validatePassword = (password: string): { isValid: boolean; message: string } => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }
  
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' };
  }
  
  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }
  
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }
  
  return { isValid: true, message: 'Password is valid' };
};
